import React, { useState } from "react";
import { PaystackButton } from "react-paystack";

import "./CSS/Consultation.css";
import axios from "../lib/axios.js";
import { useConsultationStore } from "../stores/useConsultationStores";

const INITIAL_FORM_DATA = {
  name: "",
  email: "",
  purpose: "",
  purposeDescription: "",
  meetingType: "",
  timeSlot: "",
};

const CONSULTATION_PRICES = {
  "Bridal Consultation": {
    virtual: 20000,
    inPerson: 30000,
  },
  "Bespoke Services": 10000,
  "General Enquiries": 5000,
  Other: 5000,
};

const Consultation = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [floatingMessage, setFloatingMessage] = useState("");

  const { sendConsultationRequest, isLoading } =
    useConsultationStore();

  // Create React App environment variable
  const publicKey =
    process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "";

  // Determine the selected meeting mode
  const mode =
    formData.meetingType === "Virtual Meeting"
      ? "virtual"
      : formData.meetingType === "Physical Meeting"
      ? "inPerson"
      : null;

  // Get the selected consultation price
  const selectedPrice =
    CONSULTATION_PRICES[formData.purpose];

  const amount =
    typeof selectedPrice === "number"
      ? selectedPrice
      : selectedPrice && mode
      ? selectedPrice[mode] || 0
      : 0;

  const emailIsValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      formData.email.trim()
    );

  const consultationReady = Boolean(
    formData.name.trim() &&
      emailIsValid &&
      formData.purpose &&
      formData.meetingType &&
      formData.timeSlot &&
      (formData.purpose !== "Other" ||
        formData.purposeDescription.trim()) &&
      amount > 0
  );

  const paymentReady = Boolean(
    consultationReady &&
      publicKey &&
      !isLoading
  );

  const showFloatingMessage = (
    message,
    duration = 10000
  ) => {
    setFloatingMessage(message);

    window.setTimeout(() => {
      setFloatingMessage("");
    }, duration);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));
  };

  const handlePaymentSuccess = async (
    response
  ) => {
    const reference =
      response.reference || response.trxref;

    if (!reference) {
      showFloatingMessage(
        "Payment reference missing. Please contact support."
      );
      return;
    }

    try {
      const verifyResponse = await axios.post(
        "/payments/paystack/verify",
        {
          reference,
          consultationData: {
            ...formData,
            amountPaid: amount,
            paymentDate:
              new Date().toISOString(),
          },
        }
      );

      const result = verifyResponse.data;

      if (!result.success) {
        showFloatingMessage(
          "Payment verification failed. Please contact support."
        );
        return;
      }

      const emailResult =
        await sendConsultationRequest({
          ...formData,
          amountPaid: amount,
          reference,
          paymentDate:
            new Date().toISOString(),
        });

      if (emailResult === false) {
        showFloatingMessage(
          "Payment verified, but the confirmation email could not be sent. Please contact support."
        );
      } else {
        showFloatingMessage(
          `Consultation booked successfully. Reference: ${reference}`
        );
      }

      setFormData(INITIAL_FORM_DATA);
    } catch (error) {
      console.error(
        "Consultation payment verification error:",
        error.response?.data || error.message
      );

      showFloatingMessage(
        error.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    }
  };

  const componentProps = {
    email: formData.email.trim(),
    amount: amount * 100,
    publicKey,
    currency: "NGN",
    text: `Pay ₦${amount.toLocaleString()} & Book Consultation`,
    onSuccess: handlePaymentSuccess,
    onClose: () =>
      showFloatingMessage(
        "Payment process cancelled.",
        4000
      ),
    metadata: {
      name: formData.name.trim(),
      purpose: formData.purpose,
      purposeDescription:
        formData.purpose === "Other"
          ? formData.purposeDescription.trim()
          : "",
      meetingType: formData.meetingType,
      timeSlot: formData.timeSlot,
    },
  };

  const disabledButtonText = () => {
    if (!publicKey) {
      return "PAYMENT NOT CONFIGURED";
    }

    if (!formData.purpose) {
      return "SELECT CONSULTATION PURPOSE";
    }

    if (!formData.meetingType) {
      return "SELECT MEETING TYPE";
    }

    if (amount <= 0) {
      return "SELECT PURPOSE AND MEETING TYPE";
    }

    return "COMPLETE ALL REQUIRED FIELDS";
  };

  return (
    <div className="consultation-page">
      <h1>CONTACT US</h1>

      <section className="consultation-description">
        <p>
          We offer professional tailoring
          consultations to ensure you get a perfect
          fit for your outfits. Whether it&apos;s
          custom tailoring, adjustments, or bridal
          tailoring, we&apos;re here to help. Book
          your appointment today!
        </p>
      </section>

      <form
        className="consultation-form"
        onSubmit={(event) =>
          event.preventDefault()
        }
      >
        {/* Name */}
        <div className="form-group">
          <label htmlFor="name">Name</label>

          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            autoComplete="name"
            required
          />
        </div>

        {/* Email */}
        <div className="form-group">
          <label htmlFor="email">Email</label>

          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            autoComplete="email"
            required
          />
        </div>

        {/* Consultation purpose */}
        <div className="form-group">
          <label htmlFor="purpose">
            Purpose
          </label>

          <select
            id="purpose"
            name="purpose"
            value={formData.purpose}
            onChange={handleChange}
            required
          >
            <option value="">
              Select Purpose
            </option>

            <option value="Bridal Consultation">
              Bridal Consultation — ₦20,000 Virtual
              / ₦30,000 In-Person
            </option>

            <option value="Bespoke Services">
              Bespoke Services — ₦10,000
            </option>

            <option value="General Enquiries">
              General Enquiries — ₦5,000
            </option>

            <option value="Other">
              Other — ₦5,000
            </option>
          </select>
        </div>

        {/* Other purpose description */}
        {formData.purpose === "Other" && (
          <div className="form-group">
            <label htmlFor="purposeDescription">
              Please describe your request
            </label>

            <textarea
              id="purposeDescription"
              name="purposeDescription"
              value={
                formData.purposeDescription
              }
              onChange={handleChange}
              placeholder="Enter details here..."
              rows={4}
              required
            />
          </div>
        )}

        {/* Meeting type */}
        <div className="form-group consultation-meeting-types">
          <label>Meeting Type</label>

          <div>
            <label htmlFor="virtualMeeting">
              Virtual Meeting

              <input
                type="radio"
                id="virtualMeeting"
                name="meetingType"
                value="Virtual Meeting"
                checked={
                  formData.meetingType ===
                  "Virtual Meeting"
                }
                onChange={handleChange}
                required
              />
            </label>
          </div>

          <div>
            <label htmlFor="physicalMeeting">
              Physical Meeting

              <input
                type="radio"
                id="physicalMeeting"
                name="meetingType"
                value="Physical Meeting"
                checked={
                  formData.meetingType ===
                  "Physical Meeting"
                }
                onChange={handleChange}
                required
              />
            </label>
          </div>
        </div>

        {/* Time */}
        <div className="form-group">
          <label htmlFor="timeSlot">
            Preferred Time Slot
          </label>

          <input
            type="datetime-local"
            id="timeSlot"
            name="timeSlot"
            value={formData.timeSlot}
            onChange={handleChange}
            required
          />
        </div>

        {/* Consultation fee */}
        {amount > 0 && (
          <p className="consultation-booking-note">
            Consultation fee: ₦
            {amount.toLocaleString()}
          </p>
        )}

        {/* Active Paystack button */}
        {paymentReady ? (
          <PaystackButton
            className="paystack-button"
            {...componentProps}
          />
        ) : (
          <button
            type="button"
            className="paystack-button"
            disabled
          >
            {isLoading
              ? "PROCESSING..."
              : disabledButtonText()}
          </button>
        )}
      </form>

      {floatingMessage && (
        <div
          className="floating-message"
          role="status"
          aria-live="polite"
        >
          {floatingMessage}
        </div>
      )}
    </div>
  );
};

export default Consultation;